import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Target, MapPin, IndianRupee, TrendingUp, Lock, Send, ShieldCheck, Hourglass, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../components/common";
import { FetchButton, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { StaffAvatar, StaffLabel, staffLabelText } from "../../components/StaffAvatar";
import { FollowUpList } from "../../components/FollowUpList";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { Textarea } from "../../components/ui/textarea";
import { api, errMsg, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { TerritoryDialog } from "../../components/TerritoryDialog";
import { MyTargetsCards } from "../../components/MyTargetsCards";
import { TargetHistoryCard } from "../../components/TargetHistoryCard";
import { useRoleModules } from "../../lib/roleModules";

const STATUS_META = {
  achieved: { label: "Achieved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  on_track: { label: "On track", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  behind: { label: "Behind pace", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  no_target: { label: "No target", cls: "bg-muted text-muted-foreground" },
};

function TargetDialog({ code, month, collector, mode, onOpenChange, onSaved }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const propose = mode === "propose";

  useEffect(() => {
    setAmount(collector?.pending_proposal?.proposed_amount != null
      ? String(collector.pending_proposal.proposed_amount)
      : (collector?.target != null ? String(collector.target) : ""));
    setNote(collector?.pending_proposal?.note || "");
  }, [collector]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { month, target_amount: Number(amount || 0) };
      if (propose && note.trim()) body.note = note.trim();
      const r = await api.put(`/t/${code}/targets/${collector.id}`, body);
      if (r.data.pending) {
        toast.success(`Proposal for ${staffLabelText(collector.name, collector.employee_id)} (${fmtINR(Number(amount || 0))}, ${month}) sent to the Municipal Admin for approval`);
      } else {
        toast.success(`Target for ${staffLabelText(collector.name, collector.employee_id)} set to ${fmtINR(Number(amount || 0))} (${month})`);
      }
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!collector} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="target-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            {propose ? <Send className="h-4 w-4" /> : <Target className="h-4 w-4" />}
            {propose ? "Propose monthly target" : "Monthly target"} — {staffLabelText(collector?.name, collector?.employee_id)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1">
            <Label>{propose ? "Proposed target" : "Target"} for {month} (₹)</Label>
            <Input type="number" min="0" step="100" value={amount} onChange={(e) => setAmount(e.target.value)}
              required autoFocus data-testid="target-amount-input" />
            {collector?.target != null && (
              <p className="text-[11px] text-muted-foreground">Current target: {fmtINR(collector.target)}{collector.set_by_name ? <> • set by <StaffLabel code={code} userId={collector.set_by} name={collector.set_by_name} /></> : ""}</p>
            )}
          </div>
          {propose && (
            <div className="space-y-1">
              <Label>Note for the Admin <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why this target?"
                data-testid="target-note-input" />
              <p className="text-[11px] text-muted-foreground">
                The proposal stays pending until the Municipal Admin approves it under Approvals → Collector targets.
              </p>
            </div>
          )}
          <Button type="submit" className="w-full" disabled={busy} data-testid="target-save-button">
            {busy ? (propose ? "Sending..." : "Saving...") : (propose ? "Send for approval" : "Save target")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Scorecard() {
  const { code } = useParams();
  // Phase 152: "scorecard.team_scorecard" (View only hides Territory edits) and "scorecard.collector_targets" (hidden -> no target actions)
  const access = useRoleModules(code);
  const teamRO = !access.canWrite("scorecard", "team_scorecard");
  const targetsVisible = access.featureAllowed("scorecard", "collector_targets");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [targetFor, setTargetFor] = useState(null);
  const [territoryFor, setTerritoryFor] = useState(null);
  const [followUpFor, setFollowUpFor] = useState(null); // Phase 105: collector whose balances-due list is open

  const [loading, setLoading] = useState(false);
  // Phase 133: the scorecard loads only on Fetch, for the month applied at that moment
  const gate = useFetchGate({ month });
  const appliedMonth = gate.applied?.month || month;
  const load = useCallback(() => {
    if (!gate.applied) return Promise.resolve();
    setLoading(true);
    return api.get(`/t/${code}/scorecard`, { params: { month: gate.applied.month } }).then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [code, gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = useLiveCollections(code);   // Phase 149
  useEffect(() => { if (gate.fetched) load().catch((e) => toast.error(errMsg(e))); }, [load, gate.fetched, gate.version, live]);

  const monthPicker = (
    <FetchForm onFetch={gate.fetch} className="flex items-center gap-2">
      <Label className="text-xs text-muted-foreground">Month</Label>
      <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
        className="w-44 cursor-pointer" data-testid="scorecard-month-input" />
      <FetchButton type="submit" loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="scorecard-fetch-button" />
    </FetchForm>
  );
  const heading = (subtitle) => (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="text-lg font-semibold font-heading flex items-center gap-2"><Target className="h-5 w-5" /> Team Scorecard</h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {monthPicker}
    </div>
  );

  if (!gate.fetched) {
    return (
      <div className="space-y-4" data-testid="scorecard-page">
        {heading("Monthly targets vs actual collections — pick a month and click Fetch")}
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="scorecard-fetch-prompt"
            subtitle="Pick the month above, then click Fetch to load your team's targets and collections." />
        </CardContent></Card>
      </div>
    );
  }
  if (!data) return <div className="space-y-4" data-testid="scorecard-page">{heading("Loading…")}<Spinner /></div>;

  const pacePct = data.days_in_month ? Math.round(data.elapsed_days * 100 / data.days_in_month) : 0;
  const mode = data.target_mode || "view";
  const canChange = (mode === "propose" || mode === "direct") && targetsVisible;

  const withdraw = async (c) => {
    try {
      await api.put(`/t/${code}/targets/proposals/${c.pending_proposal.id}/withdraw`);
      toast.success(`Proposal for ${staffLabelText(c.name, c.employee_id)} withdrawn`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="scorecard-page">
      {heading(`Monthly targets vs actual collections • day ${data.elapsed_days} of ${data.days_in_month} (${pacePct}% of month elapsed)`)}

      <div className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${mode === "view" ? "bg-muted/40 text-muted-foreground" : "bg-primary/[0.04] border-primary/20"}`}
        data-testid={`scorecard-target-mode-${mode}`}>
        {mode === "view" ? <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : mode === "propose" ? <Send className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" /> : <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />}
        <span>
          {data.target_mode_hint}
          {data.pending_proposals > 0 && <> <b data-testid="scorecard-pending-count">{data.pending_proposals}</b> proposal{data.pending_proposals === 1 ? "" : "s"} awaiting Admin approval.</>}
        </span>
      </div>

      {data.my_targets && (
        <div className="space-y-1.5" data-testid="scorecard-my-targets">
          <p className="text-sm font-medium">My targets for {appliedMonth}</p>
          <MyTargetsCards mt={data.my_targets} />
        </div>
      )}

      {data.my_targets && <TargetHistoryCard code={code} endMonth={appliedMonth} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Team target</p>
          <p className="text-lg font-semibold tabular-nums" data-testid="scorecard-team-target">{fmtINR(data.team_target)}</p>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" /> Collected (MTD)</p>
          <p className="text-lg font-semibold tabular-nums" data-testid="scorecard-team-collected">{fmtINR(data.team_collected)}</p>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Achievement</p>
          <p className="text-lg font-semibold tabular-nums">{data.team_achievement_pct != null ? `${data.team_achievement_pct}%` : "—"}</p>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Collectors</p>
          <p className="text-lg font-semibold tabular-nums">{data.collectors.length}</p>
        </CardContent></Card>
      </div>

      {data.collectors.length === 0 ? (
        <EmptyState title="No collectors mapped to you" subtitle="Ask the municipal admin to map tax collectors to you in Users & Staff." />
      ) : (
        <div className="space-y-3" data-testid="scorecard-list">
          {data.collectors.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.no_target;
            const pct = c.target ? Math.min(100, Math.round((c.collected_mtd / c.target) * 100)) : 0;
            return (
              <Card key={c.id} className="shadow-sm" data-testid={`scorecard-row-${c.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex items-center gap-3">
                      <StaffAvatar code={code} userId={c.id} name={c.name} size="md" />
                      <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <StaffLabel code={code} userId={c.id} name={c.name} empCode={c.employee_id} />
                        {c.parent_user_name && <Badge variant="outline" className="text-[10px]">Sub-user of <StaffLabel code={code} userId={c.parent_user_id} name={c.parent_user_name} className="ml-1" /></Badge>}
                        <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {c.ward_names.length > 0 ? `Wards: ${c.ward_names.join(", ")}` : "No wards assigned"}
                        {c.mohalla_names.length > 0 && ` • Mohallas: ${c.mohalla_names.slice(0, 4).join(", ")}${c.mohalla_names.length > 4 ? "…" : ""}`}
                      </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setFollowUpFor(c)} data-testid={`scorecard-follow-up-${c.id}`}>
                        <Wallet className="h-4 w-4 mr-1.5" /> Follow-Up
                      </Button>
                      {!teamRO && <Button variant="outline" size="sm" onClick={() => setTerritoryFor(c)} data-testid={`scorecard-territory-${c.id}`}>
                        <MapPin className="h-4 w-4 mr-1.5" /> Territory
                      </Button>}
                      {canChange && (
                        <Button size="sm" onClick={() => setTargetFor(c)} data-testid={`scorecard-set-target-${c.id}`}>
                          {mode === "propose" ? <Send className="h-4 w-4 mr-1.5" /> : <Target className="h-4 w-4 mr-1.5" />}
                          {mode === "propose" ? (c.pending_proposal ? "Edit proposal" : "Propose target") : (c.target != null ? "Edit target" : "Set target")}
                        </Button>
                      )}
                    </div>
                  </div>
                  {c.pending_proposal && (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900"
                      data-testid={`scorecard-pending-${c.id}`}>
                      <span className="flex items-center gap-1.5">
                        <Hourglass className="h-3.5 w-3.5" />
                        Proposed <b className="tabular-nums">{fmtINR(c.pending_proposal.proposed_amount)}</b> by <StaffLabel code={code} userId={c.pending_proposal.proposed_by} name={c.pending_proposal.proposed_by_name} /> · awaiting Admin approval
                        {c.pending_proposal.note && <span className="text-amber-800/80">— {c.pending_proposal.note}</span>}
                      </span>
                      {canChange && (
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-amber-900 hover:text-amber-950" onClick={() => withdraw(c)}
                          data-testid={`scorecard-withdraw-${c.id}`}>Withdraw</Button>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div><p className="text-[11px] text-muted-foreground">Target</p><p className="font-semibold tabular-nums">{c.target != null ? fmtINR(c.target) : "—"}</p></div>
                    <div><p className="text-[11px] text-muted-foreground">Collected (MTD)</p><p className="font-semibold tabular-nums">{fmtINR(c.collected_mtd)}</p></div>
                    <div><p className="text-[11px] text-muted-foreground">Expected by today</p><p className="font-semibold tabular-nums">{c.expected_to_date != null ? fmtINR(c.expected_to_date) : "—"}</p></div>
                    <div><p className="text-[11px] text-muted-foreground">Today</p><p className="font-semibold tabular-nums">{fmtINR(c.collected_today)} <span className="text-[11px] text-muted-foreground font-normal">• {c.receipts} receipts</span></p></div>
                  </div>
                  {c.target != null && (
                    <div className="flex items-center gap-3">
                      <Progress value={pct} className="h-2" />
                      <span className="text-xs tabular-nums text-muted-foreground w-14 text-right">{c.achievement_pct != null ? `${c.achievement_pct}%` : "0%"}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <TargetDialog code={code} month={appliedMonth} collector={targetFor} mode={mode}
        onOpenChange={(v) => !v && setTargetFor(null)} onSaved={load} />
      <TerritoryDialog code={code} user={territoryFor} open={!!territoryFor}
        onOpenChange={(v) => !v && setTerritoryFor(null)} onSaved={load} />

      {/* Phase 105: a collector's "top balances due" list, so the supervisor sees where they should go first */}
      <Sheet open={!!followUpFor} onOpenChange={(v) => !v && setFollowUpFor(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="scorecard-follow-up-sheet">
          {followUpFor && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-destructive" /> Follow-Up — <StaffLabel code={code} userId={followUpFor.id} name={followUpFor.name} empCode={followUpFor.employee_id} />
                </SheetTitle>
                <SheetDescription>
                  Properties in this collector's wards, mohallas and assignments that still owe money — highest balance first. Use Export Excel to hand the list out at the morning briefing.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4">
                <FollowUpList key={followUpFor.id} endpoint={`/t/${code}/field/follow-up/${followUpFor.id}`}
                  exportEndpoint={`/t/${code}/field/follow-up/${followUpFor.id}/export`}
                  exportName={`${code}_follow_up_${followUpFor.employee_id || followUpFor.name || "collector"}`}
                  propertyPath={(r) => `/${code}/supervisor/properties?focus=${r.id}`} testId="scorecard-follow-up" />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
